import React, { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthenticatedUserProps } from '../../hoc/WithAuthLayout';
import { useParams } from 'react-router-dom';
import {
    getExpectedScores,
    GlobalStateProvider,
    ReportData,
} from '@neurolytics/results-template';
import '@neurolytics/results-template/dist/esm/styles.css';
import { actions as userActions } from 'store/users/users.actions';
import { selectTargetGroupsById } from '../../store/users/users.selectors';
import { actions as assessmentActions } from '../../store/assessments/assessments.actions';
import { actions as targetGroupActions } from '../../store/targetGroups/targetGroups.actions';
import { selectAssessmentsById } from '../../store/assessments/assessments.selectors';
import ResultsTopBar from './components/ResultsTopBar';
import {
    selectTargetGroupDataById,
    selectTargetGroupWorkPreferenceBaseline,
} from '../../store/targetGroups/targetGroups.selectors';
import { ReportViewWrapper } from './Style';
import { ResultsNavBar } from './components/ResultsNavBar/ResultsNavBar';
import { SectionId } from '../../typings';
import ResultsSidebar from './components/ResultsSidebar';
import ResultsHeader from './components/ResultsHeader';
import { getAllBaselineScoreRanges, getResultSections } from './utils';
import ResultsContent from './components/ResultsContent';
import i18n, { useTranslation } from '../../i18n';
import { i18n as i18nResultsTemplate } from '@neurolytics/results-template';
import { useGetCompany } from '../../store/hooks/useCompany';

type ReportUrlParams = {
    companySlug: string;
    assessmentId: string;
    groupId: string;
    candidateId: string;
};

const ReportView: FunctionComponent<AuthenticatedUserProps> = () => {
    const { companySlug, assessmentId, groupId, candidateId } =
        useParams<ReportUrlParams>();
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const users = useSelector(selectTargetGroupsById(groupId));
    const { company } = useGetCompany(companySlug);
    const assessments = useSelector(selectAssessmentsById);
    const participantsList = useSelector(selectTargetGroupsById(groupId));
    const workPreferenceBaseline =
        useSelector(selectTargetGroupWorkPreferenceBaseline(groupId)) || [];
    const targetGroup = useSelector(selectTargetGroupDataById(groupId));
    const targetGroupName = targetGroup ? targetGroup.name : '';
    const participant =
        candidateId && participantsList
            ? participantsList[candidateId]
            : undefined;
    const participantReport =
        participant && participant.activity && participant.activity.report
            ? participant.activity.report
            : null;
    const videoRecordingOptedOut =
        !!participant?.activity?.invitation.preferences
            ?.video_recording_opted_out;
    const groupHasOptOutQuestionnaire =
        !!targetGroup?.config?.has_opt_out_questionnaire;

    const [activeSection, setActiveSection] = useState<SectionId>('summary');

    const scores =
        candidateId &&
        users &&
        users[candidateId] &&
        users[candidateId].activity?.scores;

    const assessment =
        assessmentId && assessments[assessmentId]
            ? assessments[assessmentId].data
            : targetGroup && assessments[targetGroup.assessment_id]
            ? assessments[targetGroup.assessment_id].data
            : undefined;

    const candidate = users && candidateId && users[candidateId];

    const expectedScores =
        assessment &&
        assessment.modules &&
        getExpectedScores(
            assessment.modules,
            workPreferenceBaseline && !!workPreferenceBaseline.length,
        );

    const sections =
        expectedScores &&
        scores &&
        company &&
        getResultSections(
            expectedScores,
            scores,
            company.name,
            videoRecordingOptedOut,
            groupHasOptOutQuestionnaire,
            workPreferenceBaseline,
            t,
        );

    useEffect(() => {
        if (companySlug === 'bunq') {
            i18n.changeLanguage('en-US');
            i18nResultsTemplate.changeLanguage('en-US');
        }
    }, [companySlug]);

    useEffect(() => {
        if (companySlug && targetGroup) {
            dispatch(
                assessmentActions.getAssessment(
                    companySlug,
                    targetGroup.assessment_id,
                ),
            );
        }
    }, [companySlug, dispatch, targetGroup]);

    useEffect(() => {
        if (companySlug && groupId && candidateId) {
            dispatch(
                targetGroupActions.getTargetGroupById(
                    companySlug,
                    groupId,
                    undefined,
                ),
            );
            dispatch(
                targetGroupActions.getWorkPreferenceBaseline(
                    companySlug,
                    groupId,
                    undefined,
                ),
            );
            dispatch(userActions.getCandidate(candidateId, groupId));
            dispatch(
                userActions.getUserActivity(
                    companySlug,
                    groupId,
                    candidateId,
                    true,
                ),
            );
        }
    }, [companySlug, candidateId, dispatch, groupId]);

    // check for query params
    if (!groupId || !candidateId) {
        return <div>Failed: No scan config!</div>;
    }

    // loading
    if (
        !assessment ||
        !assessment.modules ||
        !candidate ||
        !scores ||
        !company
    ) {
        return <div>Loading...</div>;
    }

    const downloadPdf = () => {
        participantReport && window.open(participantReport.report_url);
    };

    // Motiview participants register anonymously and carry no name, so the
    // parts are joined rather than interpolated: both are undefined there.
    const candidateFullName = [candidate.first_name, candidate.last_name]
        .filter(Boolean)
        .join(' ');

    const baselineRange = getAllBaselineScoreRanges(workPreferenceBaseline);

    const initialReportData: ReportData = {
        baselineRange,
    };

    return (
        <>
            {sections && users && users[candidateId] && company && (
                <GlobalStateProvider initialData={initialReportData}>
                    <ReportViewWrapper
                        id="report_wrap"
                        aria-label="report view wrapper"
                        headerSize={activeSection === 'summary' ? 140 : 270}>
                        <ResultsTopBar
                            candidateFullName={candidateFullName}
                            onDownload={downloadPdf}
                            targetGroupName={targetGroupName}
                            reportStatus={participantReport?.status}
                        />
                        <ResultsSidebar
                            sections={sections}
                            activeSection={activeSection}
                            handleSectionNavigation={setActiveSection}
                        />
                        <ResultsHeader
                            sections={sections}
                            activeSection={activeSection}
                        />
                        <ResultsNavBar
                            sections={sections}
                            activeSection={activeSection}
                        />
                        <ResultsContent
                            sections={sections}
                            activeSection={activeSection}
                        />
                    </ReportViewWrapper>
                </GlobalStateProvider>
            )}
        </>
    );
};

export default ReportView;
